import React, { useEffect, useState } from "react";
import {
  Drawer,
  Select,
  Input,
  Button,
  Table,
  Space,
  Row,
  Col,
  Tag,
} from "antd";
import { APP_STAGES, OLIVEEB_APP_OPTIONS, CURRENCIES } from "./ChargesConfigPage";
import useChargeConfigs from "hooks/finance/useChargeConfigs";
import useFormattedDate from "hooks/useFormatDate";
import { getTagStyle } from "./ChargeConfigsTable";

const defaultFilters = {
  app: "OLIVEEB_PAYMENT_SERVICE",
  stages: ["OLITASK_MOBILE_UI_ALL", "OLITASK_MOBILE_UI_IN_PROGRESS"],
  currency: "USD",
  country: "US",
  state: "",
  county: "",
  city: "",
};

const EffectiveChargePreviewDrawer = ({ open, onClose }) => {
  const { fetchEffectiveCharges } = useChargeConfigs();
  const { formatDateTime } = useFormattedDate();
  const [filters, setFilters] = useState(defaultFilters);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // fetch whenever drawer opens
  useEffect(() => {
    if (open) {
      handlePreview(1, pagination.pageSize);
    }
  }, [open]);

  const handlePreview = async (page = 1, pageSize = 20) => {
    const currentPage = Number(page) || 1; // default to 1 if NaN
    const size = Number(pageSize) || 20;

    setLoading(true);
    try {
      const payload = {
        app: filters.app,
        stages: filters.stages,
        currency: filters.currency,
        country: filters.country || undefined,
        state: filters.state || undefined,
        county: filters.county || undefined,
        city: filters.city || undefined,
        page: currentPage - 1,
        size,
      };

      const res = await fetchEffectiveCharges(payload);

      setResults(res?.content || []);
      setPagination({
        current: (res?.pageable?.pageNumber || 0) + 1,
        pageSize: res?.pageable?.pageSize || pageSize,
        total: res?.totalElements || res?.content?.length || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pag) => {
    handlePreview(pag.current, pag.pageSize);
  };

  const columns = [
    { title: "Code", dataIndex: "code", key: "code", ellipsis: true },
    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
    { title: "Value Type", dataIndex: "valueType", key: "valueType" },
    {
      title: "Value",
      key: "value",
      render: (_, r) =>
        r.valueType === "PERCENTAGE"
          ? `${(r.value * 100).toFixed(2)}%`
          : `$${r.value.toFixed(2)}`,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => <Tag style={getTagStyle("category", cat)}>{cat}</Tag>,
    },
    { title: "ApplyOn", dataIndex: "applyOn", key: "applyOn" },
    { title: "Stage", dataIndex: "stage", key: "stage", ellipsis: true },
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (val) => (
        <span style={{ fontSize: 12 }}>{formatDateTime(val)}</span>
      ),
      ellipsis: true,
    },
    {
      title: "Effective To",
      dataIndex: "effectiveTo",
      key: "effectiveTo",
      render: (val) => (
        <span style={{ fontSize: 12 }}>{formatDateTime(val)}</span>
      ),
      ellipsis: true,
    },
  ];

  const expandedRowRender = (record) => {
    const meta = [
      { label: "Code", value: record.code || "-" },
      { label: "Name", value: record.name || "-" },
      { label: "Description", value: record.description || "-" },
      { label: "Apply On", value: record.applyOn || "-" },
      { label: "Currency", value: record.currency || "-" },
      {
        label: "Effective From",
        value: formatDateTime(record.effectiveFrom),
      },
      {
        label: "Effective To",
        value: record.effectiveTo ? formatDateTime(record.effectiveTo) : "-",
      },
      { label: "Country", value: record.country || "-" },
      { label: "State", value: record.state || "-" },
      { label: "County", value: record.county || "-" },
      { label: "City", value: record.city || "-" },
      { label: "Version", value: record.versionNumber || "-" },
    ];

    return (
      <div
        style={{
          paddingLeft: 40,
          background: "#f9fafb",
        }}
      >
        {meta.map((m) => (
          <div key={m.label} style={{ minWidth: 180 }}>
            <strong>{m.label}:</strong> {m.value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Drawer
      size="100%"
      zIndex={9999}
      open={open}
      onClose={onClose}
      styles={{ body: { paddingBottom: 80 } }}
      closable={false}
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: 1100,
            margin: "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          fontFamily: "Inter, san-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <h4 className="text-base fw-bold">Effective Charges Preview</h4>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            {/* <COutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            /> */}
          </div>
        </div>

        {/* Filter Bar */}
        <Row gutter={[10, 5]} align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Select
              placeholder="Product Backend Service"
              value={filters.app}
              style={{ width: 260 }}
              onChange={(val) => setFilters((prev) => ({ ...prev, app: val }))}
              options={OLIVEEB_APP_OPTIONS}
            />
          </Col>
          <Col>
            <Select
              placeholder="Stage"
              value={filters.stages[0]}
              style={{ width: 220 }}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, stages: [val] }))
              }
              options={APP_STAGES.map((s) => ({ value: s, label: s }))}
            />
          </Col>
          <Col>
            <Select
              placeholder="Currency"
              value={filters.currency}
              style={{ width: 120 }}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, currency: val }))
              }
              options={CURRENCIES.map((c) => ({
                value: c.value,
                label: c.label,
              }))}
            />
          </Col>
          <Col>
            <Input
              placeholder="Country"
              value={filters.country}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, country: e.target.value }))
              }
              style={{ width: 120, height: "100%" }}
            />
          </Col>
          <Col>
            <Input
              placeholder="State"
              value={filters.state}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, state: e.target.value }))
              }
              style={{ width: 120, height: "100%" }}
            />
          </Col>
          <Col>
            <Input
              placeholder="County"
              value={filters.county}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, county: e.target.value }))
              }
              style={{ width: 120, height: "100%" }}
            />
          </Col>
          <Col>
            <Input
              placeholder="City"
              value={filters.city}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, city: e.target.value }))
              }
              style={{ width: 120, height: "100%" }}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={handlePreview}>
              Preview
            </Button>
          </Col>
        </Row>

        {/* table */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={results}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} charges`,
          }}
          onChange={handleTableChange}
          expandable={{ expandedRowRender, rowExpandable: () => true }}
          scroll={{ x: 1200 }}
          size="small"
          className="custom-table"
        />
      </div>
    </Drawer>
  );
};

export default EffectiveChargePreviewDrawer;
